import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { api, getErrorMessage } from "../../../api/client.ts";
import BackupCodesList from "../../../components/BackupCodesList.tsx";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import CopyButton from "../../../components/CopyButton.tsx";
import TotpInput from "../../../components/TotpInput.tsx";
import { useAuth } from "../../../contexts/AuthContext.tsx";

interface MfaSetupDialogProps {
  onClose: () => void;
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1.5}>
      <Box
        sx={{
          width: 22,
          height: 22,
          flexShrink: 0,
          borderRadius: "50%",
          bgcolor: "action.selected",
          display: "grid",
          placeItems: "center",
          fontSize: "0.75rem",
          fontWeight: 600,
        }}
      >
        {n}
      </Box>
      <Typography variant="body2" component="div" sx={{ pt: 0.125 }}>
        {children}
      </Typography>
    </Stack>
  );
}

/** Scan, confirm a code, save the backup codes. Mounted only while open. */
export default function MfaSetupDialog({ onClose }: MfaSetupDialogProps) {
  const { user, refreshUser } = useAuth();
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const setup = useMutation({ mutationFn: api.mfaSetup, meta: { suppressErrorToast: true } });
  const verify = useMutation({
    mutationFn: (totp: string) => api.mfaVerify(totp),
    meta: { suppressErrorToast: true },
    onSuccess: async (res) => {
      setBackupCodes(res.backup_codes);
      await refreshUser();
      toast.success("Two-step verification is on");
    },
  });

  // Each setup call mints a new secret: start exactly once per opening.
  const started = useRef(false);
  const { mutate: start } = setup;
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    start();
  }, [start]);

  useEffect(() => {
    if (code.length === 6 && !verify.isPending) verify.mutate(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (backupCodes) {
    return (
      <Dialog open maxWidth="sm" fullWidth>
        <DialogTitle>Save your backup codes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            If you lose your phone, each code signs you in once. Keep them somewhere safe, like your
            password manager. They won't be shown again.
          </Typography>
          <BackupCodesList codes={backupCodes} />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const secret = setup.data?.secret;
  return (
    <Dialog open onClose={verify.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Turn on two-step verification</DialogTitle>
      <DialogContent>
        {setup.isError ? (
          <Alert severity="error">{getErrorMessage(setup.error)}</Alert>
        ) : !setup.data ? (
          <CenteredSpinner py={6} />
        ) : (
          <Stack spacing={2.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ alignItems: "center" }}>
              {/* QR codes need a light background to scan, in either theme. */}
              <Box sx={{ p: 1.5, bgcolor: "common.white", borderRadius: 1, lineHeight: 0 }}>
                <QRCodeSVG value={setup.data.provisioning_uri} size={160} />
              </Box>
              <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                <Step n={1}>Open an authenticator app, such as 1Password, Google Authenticator or Authy.</Step>
                <Step n={2}>
                  Scan the QR code, or enter this key by hand:
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                    <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.8125rem", wordBreak: "break-all" }}>
                      {secret}
                    </Box>
                    <CopyButton text={secret ?? ""} label="Copy key" sx={{ my: -0.5 }} />
                  </Box>
                </Step>
                <Step n={3}>Enter the 6-digit code the app shows.</Step>
              </Stack>
            </Stack>
            {verify.isError && <Alert severity="error">{getErrorMessage(verify.error)}</Alert>}
            <TotpInput
              value={code}
              onChange={setCode}
              autoFocus
              disabled={verify.isPending}
              error={verify.isError && code.length === 6}
              username={user?.email}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={verify.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => verify.mutate(code)}
          loading={verify.isPending}
          disabled={code.length < 6 || !setup.data}
        >
          Turn on
        </Button>
      </DialogActions>
    </Dialog>
  );
}
